import { pipe } from 'fp-ts/function';
import * as O from 'monocle-ts/Optional';
import { Action } from 'redux-saga';
import { Future, FUTURE } from 'src/modules/common/types/Future';
import {
  SHARE_PROJECT_CHANGE_ROLE,
  SHARE_PROJECT_FETCH,
  SHARE_PROJECT_REMOVE_SUBMIT,
  SHARE_PROJECT_SUBMIT,
} from 'src/modules/sharing-projects/actions/ShareProjectActions';
import { SharedUser } from 'src/modules/sharing-projects/types/SharedUser';
import { ShareProjectState } from 'src/modules/sharing-projects/types/ShareProjectState';

export function shareProjectUsersReducer(state: ShareProjectState, action: Action): ShareProjectState {
  if (SHARE_PROJECT_FETCH.isPending(action)) {
    return {
      ...state,
      data: {
        project: action.meta,
        sharedUsers: FUTURE.pending(),
        organizationUsers: FUTURE.pending(),
        removing: null,
        transferringOwnership: null,
        changingOwnRole: null,
      },
    };
  }
  if (SHARE_PROJECT_FETCH.isSuccess(action)) {
    return {
      ...state,
      data: {
        project: action.meta,
        sharedUsers: FUTURE.success(action.data.sharedUsers),
        organizationUsers: FUTURE.success(action.data.organizationUsers),
        removing: null,
        transferringOwnership: null,
        changingOwnRole: null,
      },
    };
  }
  if (SHARE_PROJECT_FETCH.isFailure(action)) {
    return {
      ...state,
      data: {
        project: action.meta,
        sharedUsers: FUTURE.failure(action.data),
        organizationUsers: FUTURE.failure(action.data),
        removing: null,
        transferringOwnership: null,
        changingOwnRole: null,
      },
    };
  }

  if (SHARE_PROJECT_SUBMIT.isSuccess(action)) {
    return pipe(state, modifyUsers(FUTURE.success(action.data)));
  }
  if (SHARE_PROJECT_REMOVE_SUBMIT.isSuccess(action)) {
    return pipe(state, modifyUsers(FUTURE.success(action.data)));
  }

  if (SHARE_PROJECT_CHANGE_ROLE.isSuccess(action)) {
    const { role, userId } = action.data;

    if (state.data && state.data.sharedUsers.state === 'success') {
      return {
        ...state,
        data: {
          ...state.data,
          sharedUsers: FUTURE.success(state.data.sharedUsers.value.map((user) => {
            if (user.id === userId) {
              return {
                ...user,
                role,
              };
            }

            return user;
          })),
        },
      };
    }

    return state;
  }

  return state;
}

function modifyUsers(
  users: Future<ReadonlyArray<SharedUser>>,
): (state: ShareProjectState) => ShareProjectState {
  return pipe(
    O.id<ShareProjectState>(),
    O.prop('data'),
    O.fromNullable,
    O.prop('sharedUsers'),
    O.modify(() => users),
  );
}
